import { useState, useEffect, useRef } from 'react';
import { IPurchaseOrder, IStatusOption } from 'interfaces';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TenantPage from 'components/common/TenantPage';
import { orderService } from 'services/api';
import CollapsableColumns from 'components/common/CollapsableColumns';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import EditIcon from '@mui/icons-material/Edit';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ReplayIcon from '@mui/icons-material/Replay';
import SendIcon from '@mui/icons-material/Send';
import { OrderDetail } from 'components/inventory/PurchaseOrders';
import {
  GridActionsCellItem,
  GridCellParams,
  GridColumns,
  GridRenderCellParams,
  GridRowParams,
  GridValueFormatterParams
} from '@mui/x-data-grid-premium';
import { EnhancedDataGrid } from 'components/common/enhanced/data-grid';
import {
  isTenantAdministrator,
  IsTenantAdministrator
} from 'components/helpers/IsTenantAdministrator';
import { Button, ButtonGroup, Dialog, DialogContent, DialogActions } from '@mui/material';
import { loadingActions } from 'features';
import { useAppDispatch } from 'app/store';
import { currencyCell } from 'components/common/enhanced/cells/currency-cell';
import dayjs from 'dayjs';
import { dateFilterOperators, filterOperators } from '../../../../constants';
import { currencyFormatter } from 'helpers/currency-formatter';
import { IsSuperAdmin } from 'components/helpers/IsSuperAdmin';
import ImportFileUploader from 'components/common/file-uploader/ImportFileUploader';
import toast from 'features/toast';
import { PurgeButton } from 'components/helpers/PurgeButton';
import { baseCell } from 'components/common/enhanced/cells/base-cell';
import ChangeOrderStatusForm from '../PurchaseOrderStatusChangeForm';
import { numberFilterOperators } from '../../../../constants';
import { isDirector } from 'components/helpers/IsDirector';
import { isTechnician } from 'components/helpers/IsTechnician';

export const PurchaseOrderList = () => {
  const translationPrefix = 'pages.orders.list';
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const refresh = useRef(null);

  // const sap_active = useAppSelector((state) => state.auth.sap_active);
  const navigate = useNavigate();

  const [selectedOrder, setSelectedOrder] = useState<IPurchaseOrder>();
  const [statusOptions, setStatusOptions] = useState<IStatusOption[]>([]);
  const [sendingOrderId, setSendingOrderId] = useState<number | null>(null);
  const isAdmin = isTenantAdministrator();
  const isDir = isDirector();

  const notCloseOrder = isDirector() || isTechnician();
  const canSeeDetails = isTenantAdministrator() || notCloseOrder;

  // Status edit dialog
  const [statusEditDialogOpen, setStatusEditDialogOpen] = useState(false);

  const editOrder = (reqId) => {
    navigate(`../orders/view/${reqId}`);
  };

  useEffect(() => {
    dispatch(loadingActions.startLoading());
    orderService.options().then((res) => {
      if (res.actions && res.actions.POST && res.actions.POST.status.choices) {
        const choices = res.actions.POST.status.choices
          .filter((choice) => choice.value !== 0)
          .map((choice) => ({
            ...choice,
            display_name: t(`entities.order_head.${choice.display_name}`)
          }));
        setStatusOptions(choices);
      }
    });
    dispatch(loadingActions.stopLoading());
  }, []);

  // ----------------------------------------------------------- UPDATE STATUS
  const openChangeStatus = (row: any) => {
    if (row.sap_id != null && row.status == 'COMPLETED') {
      toast.error(t(`${translationPrefix}.error-sap-edit`));
    } else {
      setSelectedOrder(row);
      setStatusEditDialogOpen(true);
    }
    // Apro il dialog per modificare lo stato
    if (isAdmin) {
      setStatusEditDialogOpen(true);
    }
  };

  const handleCloseChangeStatusDialog = () => {
    // Chiudo il dialog per modificare lo stato
    setSelectedOrder(null);
    setStatusEditDialogOpen(false);
  };

  const onEditOrderStatus = (status: any) => {
    orderService.update(selectedOrder.id, status).then((res) => {
      toast.success(t('actions.successfully.updated'));
      refresh.current();

      // Chiudo il dialog per modificare lo stato
      handleCloseChangeStatusDialog();
    });
  };

  const downloadAttachment = (order) => {
    if (order?.id) {
      orderService.downloadAttachment(order.id);
    }
  };

  const regenerateDocument = (order) => {
    if (order?.id) {
      orderService.regeneratePDF(order.id).then(() => {
        toast.success('PDF rigenerato.');
      });
    }
  };

  const sendOrderToSap = (order) => {
    if (!order?.id || sendingOrderId) return;
    setSendingOrderId(order.id);
    orderService
      .sendToSap(order.id)
      .then((res) => {
        const msg = res?.tracking_id
          ? t('pages.purchase-orders.view.send-to-sap-success-tracking', {
              trackingId: res.tracking_id
            })
          : t('pages.purchase-orders.view.send-to-sap-success');
        toast.success(msg);
        if (typeof refresh.current === 'function') refresh.current();
      })
      .catch(() => {
        // gli errori (400/502 con {"error": ...}) sono già mostrati dall'interceptor axios
      })
      .finally(() => setSendingOrderId(null));
  };

  const strikedThroughRenderCell = (params: GridCellParams) =>
    params.row.status === 'CANCELED' && 'line-through';

  const statusColor = (statusString: string) => {
    // Funzione per determinare il colore del testo in base allo stato, "! = important" forza il comportamento della MUI
    switch (statusString) {
      case 'ACTIVE':
        return '!text-orange-500';
      case 'COMPLETED':
        return '!text-green-500';
      default:
        return '!text-gray-500';
    }
  };

  const columns: GridColumns = [
    {
      field: 'ref',
      headerName: 'Ordine',
      filterOperators,
      sortable: true,
      cellClassName: strikedThroughRenderCell,
      flex: 1
    }
  ];

  // if (sap_active) {
  columns.push({
    field: 'sap_id',
    headerName: t(`global.sap_id`),
    filterOperators,
    sortable: true,
    width: 100,
    renderCell: baseCell
  });
  // }

  columns.push(
    {
      field: 'created_at',
      headerName: t(`entities.movement.timestamp`),
      filterOperators: dateFilterOperators,
      cellClassName: strikedThroughRenderCell,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <div>
            <span>{dayjs(params.value).format('DD/MM/YYYY')}</span>
          </div>
        );
      }
    },
    {
      field: 'status',
      headerName: t(`global.status`),
      type: 'singleSelect',
      editable: false,
      sortable: true,
      valueOptions: statusOptions.map((type) => ({
        label: type.display_name,
        value: String(type.value)
      })),
      width: 100,
      cellClassName: strikedThroughRenderCell,
      renderCell: (params) => (
        <span
          className={`${statusColor(params.value)} cursor-pointer`}
          onClick={(e) => {
            e.stopPropagation(); // ⚠️ evita che si attivino altri eventi come onRowClick
            if (notCloseOrder) return;
            openChangeStatus(params.row);
            //style={{ cursor: 'pointer', textDecoration: 'underline' }}
          }}
          // style={{ cursor: 'pointer' }}
        >
          {t(`entities.order_head.${params.value}`)}
        </span>
      )
    },
    {
      field: 'supplier',
      headerName: t(`entities.order.supplier`),
      flex: 1,
      filterOperators,
      sortable: true,
      cellClassName: strikedThroughRenderCell
    },
    {
      field: 'warehouse',
      headerName: t(`entities.order.warehouse`),
      flex: 1,
      filterOperators,
      sortable: true,
      cellClassName: strikedThroughRenderCell
    }
  );
  if (!isDir) {
    columns.push({
      field: 'total_amount',
      headerName: t(`entities.order.total_amount`),
      width: 100,
      filterOperators: numberFilterOperators,
      cellClassName: (params: GridCellParams) => params.row.status === 3 && 'line-through',
      valueFormatter: (params: GridValueFormatterParams) => {
        return currencyFormatter(params.value, { significantDigits: 3 });
      },
      renderCell: (params: GridRenderCellParams) => currencyCell(params, 3)
    });
  }
  columns.push({
    field: 'actions',
    type: 'actions',
    headerName: t(`global.actions`),
    getActions: (params: GridRowParams) => {
      const actions = [];
      if (canSeeDetails) {
        actions.push(
          <GridActionsCellItem
            key={0}
            icon={params.row.status !== 'ACTIVE' ? <RemoveRedEyeIcon /> : <EditIcon />}
            onClick={() => editOrder(params.row.id)}
            label="Edit"
          />
        );
      }
      if (params.row.attachment) {
        actions.push(
          <IsTenantAdministrator key={2}>
            <GridActionsCellItem
              icon={<CloudDownloadIcon />}
              onClick={() => downloadAttachment(params.row)}
              label="View"
            />
          </IsTenantAdministrator>
        );
      }
      actions.push(
        <IsTenantAdministrator key={3}>
          <GridActionsCellItem
            icon={<ReplayIcon />}
            onClick={() => regenerateDocument(params.row)}
            label="Regenerate"
          />
        </IsTenantAdministrator>
      );
      if (
        // sap_active &&
        params.row.status === 'COMPLETED' &&
        params.row.sap_id &&
        params.row.warehouse_is_presidio
      ) {
        actions.push(
          <IsTenantAdministrator key={4}>
            <GridActionsCellItem
              icon={<SendIcon />}
              onClick={() => sendOrderToSap(params.row)}
              label={t('pages.purchase-orders.list.send-to-sap')}
              disabled={sendingOrderId === params.row.id}
              showInMenu
            />
          </IsTenantAdministrator>
        );
      }
      return actions;
    }
  });

  return (
    <>
      <TenantPage
        title={t(`${translationPrefix}.title`)}
        subtitle={t(`${translationPrefix}.subtitle`)}
        menuRight={
          <ButtonGroup size="small">
            <NavLink to="delivery?blameMode=true">
              <Button>{t(`${translationPrefix}.delivery`)}</Button>
            </NavLink>
            <IsSuperAdmin>
              <ImportFileUploader
                service={orderService}
                importMethod={orderService.importAggregatedLinesFromFile}
                templateGetterMethod={orderService.getAggregatedImportTemplate}
              />
            </IsSuperAdmin>
            <PurgeButton service={orderService} refresh={refresh} />
          </ButtonGroup>
        }>
        <CollapsableColumns
          size="w-1/2"
          expand={false}
          onClose={() => setSelectedOrder(null)}
          contentLeft={
            <EnhancedDataGrid columns={columns} service={orderService} refresh={refresh} />
          }
          contentRight={
            <div className="mt-6">{selectedOrder && <OrderDetail order={selectedOrder} />}</div>
          }
        />

        {selectedOrder && (
          <Dialog
            open={statusEditDialogOpen}
            onClose={handleCloseChangeStatusDialog}
            fullWidth
            maxWidth="md">
            {/* Dialog for inserting new documents */}
            <div className="text-2xl p-5">
              {t('entities.order_head.edit-status-title', { ref: selectedOrder.ref })}
            </div>
            <DialogContent>
              {/* Form for inserting new documents */}
              <ChangeOrderStatusForm onSubmit={onEditOrderStatus} status={statusOptions} />
            </DialogContent>
            <DialogActions>
              {/* Buttons for closing the dialog */}
              <Button onClick={handleCloseChangeStatusDialog} color="error">
                {t('actions.reset')}
              </Button>
              <Button type="submit" form="editOrderStatus">
                {t(`actions.confirm`)}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </TenantPage>
    </>
  );
};

export default PurchaseOrderList;
